import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { api, formatApiError } from "../lib/api";
import { Check, Loader2, CalendarClock, Sparkles, ChevronLeft, Crown, Shield } from "lucide-react";
import { toast } from "sonner";

function PlanCard({ planKey, price, currency, selected, onPick, t, savingsBadge }) {
    const isMonthly = planKey === "monthly";
    const accent = isMonthly
        ? "from-sky-50 to-white border-sky-200"
        : "from-amber-50 to-white border-amber-200";
    const ring = selected
        ? isMonthly
            ? "ring-2 ring-sky-500"
            : "ring-2 ring-amber-500"
        : "ring-0";
    const Icon = isMonthly ? CalendarClock : Crown;
    const iconColor = isMonthly ? "text-sky-700" : "text-amber-700";

    return (
        <button
            type="button"
            onClick={onPick}
            data-testid={`sub-card-${planKey}`}
            className={`group relative flex h-full w-full flex-col overflow-hidden rounded-3xl border bg-gradient-to-br p-6 text-right shadow-sm transition hover:-translate-y-1 hover:shadow-xl ${accent} ${ring}`}
        >
            {savingsBadge ? (
                <div
                    className="absolute top-4 left-4 rounded-full bg-emerald-600 px-2.5 py-1 text-[10px] font-semibold text-white shadow"
                    data-testid={`sub-savings-${planKey}`}
                >
                    {savingsBadge}
                </div>
            ) : null}

            <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/90 ring-1 ring-inset ring-neutral-200">
                    <Icon className={`h-6 w-6 ${iconColor}`} />
                </div>
                <div className="flex-1">
                    <h3 className="font-heading text-lg font-bold sm:text-xl">
                        {isMonthly ? t.subMonthly : t.subYearly}
                    </h3>
                    <p className="mt-0.5 text-xs text-neutral-500">
                        {isMonthly ? t.subMonthlyDuration : t.subYearlyDuration}
                    </p>
                </div>
            </div>

            <div className="mt-5 flex items-baseline gap-2">
                <span className="font-numbers text-4xl font-extrabold text-neutral-900" data-testid={`sub-price-${planKey}`}>
                    {price > 0 ? price : "—"}
                </span>
                <span className="text-sm font-semibold text-neutral-500">{currency}</span>
                <span className="text-xs text-neutral-500">
                    / {isMonthly ? t.subPerMonth : t.subPerYear}
                </span>
            </div>

            <ul className="mt-5 space-y-2 text-xs text-neutral-700 sm:text-sm">
                <li className="flex items-start gap-2">
                    <Check className={`mt-0.5 h-4 w-4 shrink-0 ${iconColor}`} />
                    <span>{t.subFeatureFullAccess}</span>
                </li>
                <li className="flex items-start gap-2">
                    <Check className={`mt-0.5 h-4 w-4 shrink-0 ${iconColor}`} />
                    <span>{t.subFeatureUnlimitedLedgers}</span>
                </li>
                <li className="flex items-start gap-2">
                    <Check className={`mt-0.5 h-4 w-4 shrink-0 ${iconColor}`} />
                    <span>{isMonthly ? t.subFeatureMonthly : t.subFeatureYearly}</span>
                </li>
                <li className="flex items-start gap-2">
                    <Shield className={`mt-0.5 h-4 w-4 shrink-0 ${iconColor}`} />
                    <span>{t.subFeatureTestMode}</span>
                </li>
            </ul>

            <div
                className={`mt-6 inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                    selected
                        ? isMonthly
                            ? "bg-sky-700 text-white"
                            : "bg-amber-700 text-white"
                        : "bg-white text-neutral-700 ring-1 ring-neutral-200"
                }`}
            >
                {selected ? (
                    <>
                        <Check className="h-4 w-4" /> {t.subSelected}
                    </>
                ) : (
                    <>
                        {t.subChooseThis}
                        <ChevronLeft className="h-4 w-4 transition group-hover:-translate-x-1" />
                    </>
                )}
            </div>
        </button>
    );
}

export default function SubscriptionGate() {
    const { t } = useLang();
    const { company, refresh } = useAuth();
    const navigate = useNavigate();

    const [defaults, setDefaults] = useState({ monthly_price: 0, yearly_price: 0, currency: "ر.س" });
    const [picked, setPicked] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const { data } = await api.get("/subscription/status");
                if (cancel) return;
                if (data?.defaults) setDefaults(data.defaults);
                // If already subscribed (active/grace/free) — redirect home.
                if (["active", "grace", "free"].includes(data?.status)) {
                    navigate("/", { replace: true });
                    return;
                }
            } catch {
                /* keep gate */
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => {
            cancel = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const savings = useMemo(() => {
        const m = parseFloat(defaults.monthly_price) || 0;
        const y = parseFloat(defaults.yearly_price) || 0;
        if (m <= 0 || y <= 0) return null;
        const fullYear = m * 12;
        if (y < fullYear) {
            const pct = Math.round(((fullYear - y) / fullYear) * 100);
            return pct > 0 ? `${t.subSavings} ${pct}%` : null;
        }
        return null;
    }, [defaults, t]);

    const submit = async () => {
        if (!picked) return;
        setSaving(true);
        try {
            await api.post("/subscription/checkout", { plan: picked });
            await refresh?.();
            toast.success(t.subActivatedToast);
            navigate("/", { replace: true });
        } catch (e) {
            toast.error(formatApiError(e) || t.subActivateFail);
        } finally {
            setSaving(false);
            setConfirmOpen(false);
        }
    };

    if (loading) {
        return (
            <AppShell>
                <div className="h-64 animate-pulse rounded-2xl bg-neutral-200/60" />
            </AppShell>
        );
    }

    const isEmployee = company?.account_type === "employee";

    return (
        <AppShell>
            <div className="mb-6 sm:mb-8">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 sm:text-sm">
                    <Sparkles className="me-1 inline h-4 w-4" />
                    {t.subKicker}
                </p>
                <h1 className="font-heading text-2xl font-bold sm:text-3xl lg:text-4xl">
                    {t.subTitle}
                </h1>
                <p className="mt-2 text-xs text-neutral-500 sm:text-sm">{t.subSubtitle}</p>
            </div>

            {isEmployee ? (
                <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-5 text-sm text-amber-900">
                    {t.subEmployeeNotice}
                </div>
            ) : (
                <>
                    <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50/60 px-3 py-1 text-[11px] font-semibold text-indigo-800">
                        <Shield className="h-3.5 w-3.5" />
                        {t.subTestModeBadge}
                    </div>

                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                        <PlanCard
                            planKey="monthly"
                            price={defaults.monthly_price}
                            currency={defaults.currency}
                            selected={picked === "monthly"}
                            onPick={() => setPicked("monthly")}
                            t={t}
                        />
                        <PlanCard
                            planKey="yearly"
                            price={defaults.yearly_price}
                            currency={defaults.currency}
                            selected={picked === "yearly"}
                            onPick={() => setPicked("yearly")}
                            t={t}
                            savingsBadge={savings}
                        />
                    </div>

                    <div className="sticky bottom-4 z-20 mt-6 flex items-center justify-end gap-3 rounded-2xl border border-neutral-200 bg-white/95 p-3 shadow-lg backdrop-blur sm:p-4">
                        <Button
                            type="button"
                            onClick={() => (picked ? setConfirmOpen(true) : toast.error(t.subRequired))}
                            disabled={saving || !picked}
                            className="h-11 gap-2 bg-amber-700 hover:bg-amber-800"
                            data-testid="sub-confirm-btn"
                        >
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            {t.subActivateBtn}
                        </Button>
                    </div>

                    <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>{t.subConfirmTitle}</AlertDialogTitle>
                                <AlertDialogDescription>
                                    {t.subConfirmBody}
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel data-testid="sub-confirm-cancel">
                                    {t.cancel || "إلغاء"}
                                </AlertDialogCancel>
                                <AlertDialogAction onClick={submit} data-testid="sub-confirm-ok">
                                    {t.subActivateBtn}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </>
            )}
        </AppShell>
    );
}
